<script lang="ts">
export interface UserPrompt {
  prompt: string
  preferredFormat: Nullable<ResponseFormat>
}
</script>

<script setup lang="ts">
import * as yup from 'yup'
import RateBoardModeInfo from '@/components/assistant/RateBoardModeInfo.vue'
import SendButton from '@/components/assistant/chat/SendButton.vue'
import SquaredIconButton from '@/components/assistant/SquaredIconButton.vue'
import SuggestionRow from '@/components/assistant/chat/SuggestionRow.vue'
import type {Nullable} from '@/types'
import type {Suggestion} from '@/components/assistant/types'
import {
  PROMPT_MAX_INPUT_CHARS,
  PROMPT_MAX_TEXTAREA_ROWS,
  PROMPT_MIN_INPUT_CHARS,
  PROMPT_TEXTAREA_LINE_HEIGHT_FALLBACK_PX,
} from '@/components/assistant/constants'
import {ResponseFormat} from '@/client/mentor/MessagesClient'
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import {useField, useForm} from 'vee-validate'
import {useI18n} from 'vue-i18n'
import {useResizeObserver} from '@vueuse/core'
import {useScrollState} from '@/composables/useScrollState'

const emits = defineEmits<{
  input: [UserPrompt]
}>()

const props = defineProps<{
  disabled?: boolean
  sending?: boolean
  visible?: boolean
  openSuggestions?: boolean
}>()

const {t} = useI18n()

const {meta, resetForm} = useForm<UserPrompt>({
  initialValues: {
    prompt: '',
    preferredFormat: null,
  },
  validationSchema: yup.object({
    prompt: yup
      .string()
      .required(t('validation.required'))
      .min(PROMPT_MIN_INPUT_CHARS, t('validation.min'))
      .max(PROMPT_MAX_INPUT_CHARS, t('validation.max')),
  }),
})

const {value: promptValue} = useField<string>('prompt')
const {value: preferredFormat} = useField<Nullable<ResponseFormat>>('preferredFormat')

const promptTextarea = ref<HTMLTextAreaElement | null>(null)
const isExpanded = ref(false)
const isManuallyCollapsed = ref(false)
const canExpand = ref(false)
const {isScrollable, isAtStart, isAtEnd, updateScrollState} = useScrollState(promptTextarea)
let textareaWidth = 0

useResizeObserver(promptTextarea, ([entry]) => {
  if (entry.contentRect.width === textareaWidth) return
  textareaWidth = entry.contentRect.width
  resizePromptTextarea()
})

const canSubmit = computed(() => !props.disabled && meta.value.dirty && meta.value.valid)

watch(promptValue, () => {
  if (!promptValue.value) isManuallyCollapsed.value = false
  isExpanded.value = !isManuallyCollapsed.value
  nextTick(resizePromptTextarea)
})

watch(
  () => props.visible,
  async (isVisible) => {
    if (isVisible) {
      await nextTick()
      resizePromptTextarea()
    }
  },
  {immediate: true}
)

onMounted(() => {
  resizePromptTextarea()
})

function onSelectSuggestion(suggestion: Suggestion) {
  promptValue.value = suggestion.fullPrompt ?? `${suggestion.mainPart} ${suggestion.extendedPrompt}`
  preferredFormat.value = suggestion.format ?? null
}

function resizePromptTextarea() {
  if (!promptTextarea.value) return

  const lineHeight =
    Number.parseFloat(window.getComputedStyle(promptTextarea.value).lineHeight) || PROMPT_TEXTAREA_LINE_HEIGHT_FALLBACK_PX
  const maxHeight = lineHeight * PROMPT_MAX_TEXTAREA_ROWS

  promptTextarea.value.style.height = 'auto'
  const contentHeight = promptTextarea.value.scrollHeight
  canExpand.value = contentHeight > maxHeight
  if (!canExpand.value) isExpanded.value = false
  const height = Math.min(contentHeight, isExpanded.value ? 255 : maxHeight)
  promptTextarea.value.style.height = `${height}px`
  promptTextarea.value.style.overflowY = contentHeight > height ? 'auto' : 'hidden'
  updateScrollState()
}

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  isManuallyCollapsed.value = !isExpanded.value
  resizePromptTextarea()
}

function submitForm() {
  if (!canSubmit.value) return
  emits('input', {prompt: promptValue.value, preferredFormat: preferredFormat.value})
  resetForm()
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <SuggestionRow :disabled="disabled" :open="openSuggestions" @select="onSelectSuggestion" />

    <form @submit.prevent="submitForm">
      <div class="w-full p-4 flex flex-col gap-2 bg-new-neutral-0 rounded-md">
        <div class="relative pr-1">
          <textarea
            ref="promptTextarea"
            v-model="promptValue"
            class="w-full block appearance-none outline-none resize-none border-0 bg-transparent pl-0 pr-3 py-0 leading-6 text-new-neutral-900 placeholder:text-new-neutral-300"
            :class="{'cursor-not-allowed ': disabled}"
            :disabled="disabled"
            :maxlength="PROMPT_MAX_INPUT_CHARS"
            :minlength="PROMPT_MIN_INPUT_CHARS"
            :placeholder="t('assistant.chat.prompt.placeholder')"
            rows="1"
            @input="resizePromptTextarea"
            @keydown.enter.exact.prevent="submitForm"
          />
          <div
            v-if="isScrollable && !isAtStart"
            aria-hidden="true"
            class="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-new-neutral-0 to-transparent"
          />
          <div
            v-if="isScrollable && !isAtEnd"
            aria-hidden="true"
            class="pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-new-neutral-0 to-transparent"
          />
          <button
            v-if="canExpand"
            :aria-expanded="isExpanded"
            :aria-label="t(isExpanded ? 'assistant.actions.collapse' : 'assistant.actions.expand')"
            class="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center bg-transparent text-xs text-new-neutral-500 hover:bg-transparent hover:text-new-neutral-700 focus-visible:ring-2 focus-visible:ring-new-primary-default"
            :title="t(isExpanded ? 'assistant.actions.collapse' : 'assistant.actions.expand')"
            type="button"
            @click="toggleExpanded"
          >
            <i aria-hidden="true" :class="isExpanded ? 'fa-solid fa-compress' : 'fa-solid fa-expand'" />
          </button>
        </div>

        <div class="flex flex-row gap-2 items-center">
          <span>{{ t('assistant.chat.responseFormat.label') }}</span>

          <div class="flex flex-row gap-1">
            <SquaredIconButton
              :disabled="disabled"
              icon-classes="fa-solid fa-table-cells"
              :variant="preferredFormat === ResponseFormat.TABLE ? 'secondary' : 'light'"
              @click="preferredFormat = preferredFormat === ResponseFormat.TABLE ? null : ResponseFormat.TABLE"
            />
            <SquaredIconButton
              :disabled="disabled"
              icon-classes="fa-solid fa-chart-column"
              :variant="preferredFormat === ResponseFormat.CHART ? 'secondary' : 'light'"
              @click="preferredFormat = preferredFormat === ResponseFormat.CHART ? null : ResponseFormat.CHART"
            />
          </div>

          <div class="ml-auto flex items-end gap-2">
            <RateBoardModeInfo />
            <SendButton :disabled="!canSubmit" :sending="sending" @click="submitForm"></SendButton>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
